/**
 * The "current session" notice receipts write-through needs (RD-NOTICES-V2).
 *
 * Every local receipt-store write function already receives `userId` from
 * its caller (it's the localStorage key's own scoping). None of them receive
 * `tenantId` today, and threading it through every call site across six
 * stores would be its own hand-copied key-list. Instead, `NoticeReceiptsSync`
 * (mounted once per signed-in session, keyed by `${userId}:${tenantId}`)
 * stamps the current session here on mount/update, and every store's write
 * function reads it when it enqueues a server upsert.
 *
 * Module-level, so it outlives individual component renders but NOT a
 * sign-out: `NoticeReceiptsSync` clears it on unmount (session ends), and a
 * write issued with a stale/absent session simply skips the server enqueue
 * (the local write still happens — the local cache is the truth for this
 * device regardless of server reachability).
 */

let currentSession = { tenantId: null, userId: null };

export function setNoticeReceiptsSession({ tenantId, userId } = {}) {
  currentSession = { tenantId: tenantId || null, userId: userId || null };
}

export function clearNoticeReceiptsSession() {
  currentSession = { tenantId: null, userId: null };
}

export function getNoticeReceiptsSession() {
  return currentSession;
}

/**
 * The tenantId to enqueue a server write with, or null if this write must
 * stay local-only (no session, or the write's own userId doesn't match the
 * session's — a mismatch means a stale closure from a previous session/user,
 * and a server write under the WRONG tenant would be a cross-tenant write).
 */
export function tenantIdForNoticeWrite(userId) {
  const session = currentSession;
  if (!session.tenantId || !session.userId || !userId) return null;
  return session.userId === userId ? session.tenantId : null;
}
